import React, { useState, useEffect, useCallback } from 'react';
import {
  Box,
  Paper,
  Typography,
  Button,
  TextField,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Chip,
  IconButton,
  Menu,
  MenuItem,
  Alert,
  FormControlLabel,
  Switch,
  Drawer,
  Divider,
  Grid,
  Avatar,
  CircularProgress,
} from '@mui/material';
import {
  Add as AddIcon,
  MoreVert as MoreVertIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  Block as BlockIcon,
  CheckCircle as ActivateIcon,
  Close as CloseIcon,
  Business as BusinessIcon,
  People as PeopleIcon,
  AccountTree as WorkflowIcon,
  PlayCircle as ExecutionsIcon,
  DeleteSweep as BulkDeleteIcon,
} from '@mui/icons-material';
import { DataGrid, GridColDef, GridRenderCellParams } from '@mui/x-data-grid';
import { adminService } from '../../services/admin.service';
import { Tenant, TenantCreateRequest, TenantStats, SystemUser } from '../../types/admin.types';
import { LoadingSpinner } from '../../components/common/LoadingSpinner';
import { enqueueSnackbar } from 'notistack';
import { PaginatedResponse } from '../../types/api.types';

// ── Tenant Detail Drawer ──────────────────────────────────────────────────────

const ROLE_COLOR: Record<string, 'default' | 'primary' | 'warning' | 'info' | 'success'> = {
  system_admin: 'primary',
  owner: 'warning',
  admin: 'info',
  editor: 'success',
  viewer: 'default',
};

interface StatCardProps { icon: React.ReactNode; label: string; value: number | string; color?: string; }
const StatCard: React.FC<StatCardProps> = ({ icon, label, value, color = 'primary.main' }) => (
  <Box sx={{ p: 2, border: '1px solid', borderColor: 'divider', borderRadius: 2, display: 'flex', alignItems: 'center', gap: 1.5 }}>
    <Box sx={{ color, display: 'flex' }}>{icon}</Box>
    <Box>
      <Typography variant="h6" fontWeight={700} lineHeight={1}>{value}</Typography>
      <Typography variant="caption" color="text.secondary">{label}</Typography>
    </Box>
  </Box>
);

interface TenantDetailDrawerProps {
  tenant: Tenant | null;
  open: boolean;
  onClose: () => void;
}

const TenantDetailDrawer: React.FC<TenantDetailDrawerProps> = ({ tenant, open, onClose }) => {
  const [stats, setStats] = useState<TenantStats | null>(null);
  const [users, setUsers] = useState<SystemUser[]>([]);
  const [usersLoading, setUsersLoading] = useState(false);
  const [statsLoading, setStatsLoading] = useState(false);
  const [statsError, setStatsError] = useState<string | null>(null);
  const [usersError, setUsersError] = useState<string | null>(null);
  const [userPage, setUserPage] = useState(0);
  const [userPageSize, setUserPageSize] = useState(10);
  // userRowCount seeds from the tenant list value so the header shows a number immediately
  const [userRowCount, setUserRowCount] = useState(tenant?.user_count ?? 0);

  const loadStats = useCallback(async () => {
    if (!tenant) return;
    setStatsLoading(true);
    setStatsError(null);
    try {
      const data = await adminService.getTenantStats(tenant.id);
      setStats(data);
    } catch (err: any) {
      const msg = err?.response?.data?.error || err?.message || 'Failed to load statistics';
      setStatsError(msg);
    } finally {
      setStatsLoading(false);
    }
  }, [tenant]);

  const loadUsers = useCallback(async () => {
    if (!tenant) return;
    setUsersLoading(true);
    setUsersError(null);
    try {
      const res = await adminService.getUsers({
        organization: tenant.id,
        page: userPage + 1,
        page_size: userPageSize,
      } as any);
      setUsers(res.results ?? []);
      setUserRowCount(res.count ?? 0);
    } catch (err: any) {
      const msg = err?.response?.data?.error || err?.message || 'Failed to load users';
      setUsersError(msg);
    } finally {
      setUsersLoading(false);
    }
  }, [tenant, userPage, userPageSize]);

  // Reset and reload when a different tenant is opened
  useEffect(() => {
    if (open && tenant) {
      setStats(null);
      setUsers([]);
      setStatsError(null);
      setUsersError(null);
      setUserPage(0);
      // Seed the row count from the list value so the header shows immediately
      setUserRowCount(tenant.user_count ?? 0);
      loadStats();
      loadUsers();
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, tenant?.id]);

  // Reload users when page/size changes (but not on initial open)
  useEffect(() => {
    if (open && tenant) loadUsers();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [userPage, userPageSize]);

  const userCols: GridColDef[] = [
    {
      field: 'avatar', headerName: '', width: 48, sortable: false,
      renderCell: (p: GridRenderCellParams) => {
        const u = p.row as SystemUser;
        return (
          <Avatar sx={{ width: 28, height: 28, fontSize: 12 }}>
            {u.first_name?.[0]}{u.last_name?.[0]}
          </Avatar>
        );
      },
    },
    {
      field: 'name', headerName: 'Name', flex: 1, minWidth: 140,
      valueGetter: (params: any) => `${params.row?.first_name ?? ''} ${params.row?.last_name ?? ''}`.trim(),
    },
    { field: 'email', headerName: 'Email', flex: 1, minWidth: 180 },
    {
      field: 'role', headerName: 'Role', width: 110,
      renderCell: (p: GridRenderCellParams) => (
        <Chip label={String(p.value)} size="small" color={ROLE_COLOR[p.value as string] ?? 'default'} />
      ),
    },
    {
      field: 'is_active', headerName: 'Active', width: 80,
      renderCell: (p: GridRenderCellParams) => (
        <Chip label={p.value ? 'Yes' : 'No'} size="small" color={p.value ? 'success' : 'default'} />
      ),
    },
    {
      field: 'last_login', headerName: 'Last Login', width: 160,
      renderCell: (p: GridRenderCellParams) =>
        p.value ? new Date(p.value as string).toLocaleString() : 'Never',
    },
  ];

  return (
    <Drawer
      anchor="right"
      open={open}
      onClose={onClose}
      PaperProps={{ sx: { width: { xs: '100%', sm: 600, md: 720 }, p: 0 } }}
    >
      {/* Header */}
      <Box sx={{ p: 2.5, display: 'flex', alignItems: 'flex-start', gap: 1, borderBottom: '1px solid', borderColor: 'divider' }}>
        <BusinessIcon sx={{ mt: 0.5, color: 'primary.main' }} />
        <Box flex={1}>
          <Typography variant="h6" fontWeight={700}>{tenant?.name}</Typography>
          <Typography variant="body2" color="text.secondary">/{tenant?.slug}</Typography>
          <Box display="flex" gap={1} mt={0.5}>
            <Chip
              label={tenant?.is_active ? 'Active' : 'Inactive'}
              size="small"
              color={tenant?.is_active ? 'success' : 'default'}
            />
            <Chip label={`Created ${tenant ? new Date(tenant.created_at).toLocaleDateString() : ''}`} size="small" variant="outlined" />
          </Box>
        </Box>
        <IconButton onClick={onClose} size="small"><CloseIcon /></IconButton>
      </Box>

      <Box sx={{ p: 2.5, overflowY: 'auto', flex: 1 }}>
        {/* Stats */}
        <Typography variant="subtitle2" fontWeight={600} mb={1.5}>Overview</Typography>
        {statsError && (
          <Alert severity="warning" sx={{ mb: 1.5 }} onClose={() => setStatsError(null)}>
            Could not load detailed stats: {statsError}
          </Alert>
        )}
        {/* Always show cards — seed with list values, update when API responds */}
        <Grid container spacing={1.5} mb={3}>
          <Grid item xs={6} sm={3}>
            <StatCard
              icon={<PeopleIcon />}
              label="Total Users"
              value={stats?.total_users ?? tenant?.user_count ?? 0}
            />
          </Grid>
          <Grid item xs={6} sm={3}>
            <StatCard
              icon={<PeopleIcon />}
              label="Active Users"
              value={statsLoading ? '…' : (stats?.active_users ?? '—')}
              color="success.main"
            />
          </Grid>
          <Grid item xs={6} sm={3}>
            <StatCard
              icon={<WorkflowIcon />}
              label="Workflows"
              value={stats?.total_workflows ?? tenant?.workflow_count ?? 0}
              color="info.main"
            />
          </Grid>
          <Grid item xs={6} sm={3}>
            <StatCard
              icon={<ExecutionsIcon />}
              label="Executions"
              value={statsLoading ? '…' : (stats?.total_executions ?? '—')}
              color="warning.main"
            />
          </Grid>
        </Grid>

        <Divider sx={{ mb: 2 }} />

        {/* Users */}
        <Typography variant="subtitle2" fontWeight={600} mb={1.5}>
          Users ({usersLoading ? '…' : userRowCount})
        </Typography>
        {usersError && (
          <Alert severity="error" sx={{ mb: 1.5 }} onClose={() => setUsersError(null)}>
            {usersError}
          </Alert>
        )}
        <Box sx={{ height: 380 }}>
          <DataGrid
            rows={users}
            columns={userCols}
            loading={usersLoading}
            rowCount={userRowCount}
            paginationMode="server"
            paginationModel={{ page: userPage, pageSize: userPageSize }}
            onPaginationModelChange={m => { setUserPage(m.page); setUserPageSize(m.pageSize); }}
            pageSizeOptions={[10, 25]}
            disableRowSelectionOnClick
            getRowId={r => r.id}
            density="compact"
          />
        </Box>
      </Box>
    </Drawer>
  );
};

// ── Main component ────────────────────────────────────────────────────────────

export const TenantManagement: React.FC = () => {
  const [tenants, setTenants] = useState<Tenant[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [createDialogOpen, setCreateDialogOpen] = useState(false);
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const [selectedTenant, setSelectedTenant] = useState<Tenant | null>(null);
  // detailTenant is kept separate from selectedTenant so handleMenuClose
  // (which clears selectedTenant) never clobbers the drawer's tenant reference.
  const [detailTenant, setDetailTenant] = useState<Tenant | null>(null);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [editDialogOpen, setEditDialogOpen] = useState(false);
  const [detailDrawerOpen, setDetailDrawerOpen] = useState(false);

  // Bulk selection & bulk delete
  const [rowSelectionModel, setRowSelectionModel] = useState<string[]>([]);
  const [bulkDeleteOpen, setBulkDeleteOpen] = useState(false);
  const [bulkDeleting, setBulkDeleting] = useState(false);
  const [editDraft, setEditDraft] = useState<{ name: string; slug: string; is_active: boolean }>({
    name: '',
    slug: '',
    is_active: true,
  });

  const [search, setSearch] = useState('');
  const [page, setPage] = useState(0);
  const [pageSize, setPageSize] = useState(10);
  const [rowCount, setRowCount] = useState(0);

  const [newTenant, setNewTenant] = useState<TenantCreateRequest>({
    name: '',
    slug: '',
    admin_email: '',
    admin_first_name: '',
    admin_last_name: '',
  });

  useEffect(() => {
    loadTenants();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [page, pageSize, search]);

  const loadTenants = async () => {
    try {
      setLoading(true);
      const resp: PaginatedResponse<Tenant> = await adminService.getTenants({
        page: page + 1,
        page_size: pageSize,
        search: search.trim() || undefined,
        ordering: 'name',
      });
      setTenants(resp.results || []);
      setRowCount(resp.count || 0);
    } catch (err: any) {
      setError(err.response?.data?.error || 'Failed to load tenants');
    } finally {
      setLoading(false);
    }
  };

  const handleCreateTenant = async () => {
    try {
      const created = await adminService.createTenant(newTenant);
      setCreateDialogOpen(false);
      setNewTenant({
        name: '',
        slug: '',
        admin_email: '',
        admin_first_name: '',
        admin_last_name: '',
      });
      enqueueSnackbar('Tenant created', { variant: 'success' });
      const temp = (created as any)?.admin_temp_password;
      if (temp) {
        enqueueSnackbar(`Tenant admin temp password (dev): ${temp}`, { variant: 'info' });
      }
      setPage(0);
      loadTenants();
    } catch (err: any) {
      setError(err.response?.data?.error || 'Failed to create tenant');
    }
  };

  const handleToggleStatus = async (tenant: Tenant) => {
    try {
      await adminService.updateTenant(tenant.id, { is_active: !tenant.is_active });
      enqueueSnackbar(tenant.is_active ? 'Tenant deactivated' : 'Tenant activated', { variant: 'success' });
      loadTenants();
    } catch (err: any) {
      setError(err.response?.data?.error || 'Failed to update tenant');
    }
  };

  const handleDeleteTenant = async (tenant: Tenant) => {
    try {
      await adminService.deleteTenant(tenant.id);
      enqueueSnackbar('Tenant deleted', { variant: 'success' });
      setDeleteDialogOpen(false);
      setSelectedTenant(null);
      loadTenants();
    } catch (err: any) {
      setError(err.response?.data?.error || 'Failed to delete tenant');
    }
  };

  const openEdit = (tenant: Tenant) => {
    setSelectedTenant(tenant);
    setEditDraft({
      name: tenant.name,
      slug: tenant.slug,
      is_active: !!tenant.is_active,
    });
    setEditDialogOpen(true);
  };

  const handleSaveEdit = async () => {
    if (!selectedTenant) return;
    try {
      await adminService.updateTenant(selectedTenant.id, {
        name: editDraft.name,
        slug: editDraft.slug,
        is_active: editDraft.is_active,
      });
      enqueueSnackbar('Tenant updated', { variant: 'success' });
      setEditDialogOpen(false);
      setSelectedTenant(null);
      loadTenants();
    } catch (err: any) {
      setError(err.response?.data?.error || 'Failed to update tenant');
    }
  };

  const handleMenuOpen = (event: React.MouseEvent<HTMLElement>, tenant: Tenant) => {
    setAnchorEl(event.currentTarget);
    setSelectedTenant(tenant);
  };

  const handleMenuClose = () => {
    setAnchorEl(null);
    setSelectedTenant(null);
  };

  const openDetail = (tenant: Tenant) => {
    setDetailTenant(tenant);
    setDetailDrawerOpen(true);
  };

  const handleBulkDelete = async () => {
    setBulkDeleting(true);
    const results = await Promise.allSettled(
      rowSelectionModel.map(id => adminService.deleteTenant(id))
    );
    const failed = results.filter(r => r.status === 'rejected').length;
    const succeeded = results.length - failed;
    if (succeeded > 0) enqueueSnackbar(`${succeeded} tenant${succeeded > 1 ? 's' : ''} deleted`, { variant: 'success' });
    if (failed > 0) enqueueSnackbar(`${failed} tenant${failed > 1 ? 's' : ''} could not be deleted`, { variant: 'error' });
    setBulkDeleting(false);
    setBulkDeleteOpen(false);
    setRowSelectionModel([]);
    loadTenants();
  };

  const columns: GridColDef[] = [
    {
      field: 'name', headerName: 'Name', flex: 1, minWidth: 200,
      renderCell: (params: GridRenderCellParams) => (
        <Typography
          variant="body2"
          sx={{ cursor: 'pointer', color: 'primary.main', fontWeight: 500, '&:hover': { textDecoration: 'underline' } }}
          onClick={() => openDetail(params.row as Tenant)}
        >
          {params.value as string}
        </Typography>
      ),
    },
    { field: 'slug', headerName: 'Slug', flex: 1, minWidth: 150 },
    {
      field: 'is_active',
      headerName: 'Status',
      width: 120,
      renderCell: (params: GridRenderCellParams) => (
        <Chip
          label={params.value ? 'Active' : 'Inactive'}
          color={params.value ? 'success' : 'default'}
          size="small"
        />
      ),
    },
    { field: 'user_count', headerName: 'Users', width: 100, align: 'center' },
    { field: 'workflow_count', headerName: 'Workflows', width: 100, align: 'center' },
    {
      field: 'last_activity',
      headerName: 'Last Activity',
      width: 180,
      renderCell: (params: GridRenderCellParams) => (
        params.value ? new Date(params.value).toLocaleString() : 'Never'
      ),
    },
    {
      field: 'actions',
      headerName: 'Actions',
      width: 80,
      sortable: false,
      renderCell: (params: GridRenderCellParams) => (
        <IconButton
          onClick={(e) => handleMenuOpen(e, params.row as Tenant)}
          size="small"
        >
          <MoreVertIcon />
        </IconButton>
      ),
    },
  ];

  if (loading) {
    return <LoadingSpinner />;
  }

  return (
    <Box sx={{ p: 3 }}>
      <Box display="flex" justifyContent="space-between" alignItems="center" mb={2} gap={2} flexWrap="wrap">
        <Box display="flex" alignItems="center" gap={1.5}>
          <Typography variant="h4">Tenant Management</Typography>
          {rowSelectionModel.length > 0 && (
            <Chip
              label={`${rowSelectionModel.length} selected`}
              size="small"
              color="primary"
              variant="outlined"
            />
          )}
        </Box>
        <Box display="flex" gap={1} alignItems="center" flexWrap="wrap">
          <TextField
            size="small"
            label="Search"
            value={search}
            onChange={(e) => {
              setSearch(e.target.value);
              setPage(0);
            }}
            sx={{ minWidth: 240 }}
          />
          {rowSelectionModel.length > 0 && (
            <Button
              variant="outlined"
              color="error"
              startIcon={<BulkDeleteIcon />}
              onClick={() => setBulkDeleteOpen(true)}
            >
              Delete ({rowSelectionModel.length})
            </Button>
          )}
          <Button
            variant="contained"
            startIcon={<AddIcon />}
            onClick={() => setCreateDialogOpen(true)}
          >
            Create Tenant
          </Button>
        </Box>
      </Box>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
          {error}
        </Alert>
      )}

      <Paper sx={{ height: 600, width: '100%' }}>
        <DataGrid
          rows={tenants}
          columns={columns}
          paginationMode="server"
          rowCount={rowCount}
          paginationModel={{ page, pageSize }}
          onPaginationModelChange={(model) => {
            setPage(model.page);
            setPageSize(model.pageSize);
          }}
          pageSizeOptions={[10, 25, 50, 100]}
          checkboxSelection
          disableRowSelectionOnClick
          rowSelectionModel={rowSelectionModel}
          onRowSelectionModelChange={(ids) => setRowSelectionModel(ids as string[])}
        />
      </Paper>

      {/* Create Tenant Dialog */}
      <Dialog open={createDialogOpen} onClose={() => setCreateDialogOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Create New Tenant</DialogTitle>
        <DialogContent>
          <TextField
            autoFocus
            margin="dense"
            label="Organization Name"
            fullWidth
            variant="outlined"
            value={newTenant.name}
            onChange={(e) => setNewTenant({ ...newTenant, name: e.target.value })}
          />
          <TextField
            margin="dense"
            label="Slug"
            fullWidth
            variant="outlined"
            value={newTenant.slug}
            onChange={(e) => setNewTenant({ ...newTenant, slug: e.target.value })}
            helperText="URL-friendly identifier"
          />
          <TextField
            margin="dense"
            label="Admin Email"
            fullWidth
            variant="outlined"
            type="email"
            value={newTenant.admin_email}
            onChange={(e) => setNewTenant({ ...newTenant, admin_email: e.target.value })}
          />
          <Box sx={{ display: 'flex', gap: 2 }}>
            <TextField
              margin="dense"
              label="Admin First Name"
              fullWidth
              variant="outlined"
              value={newTenant.admin_first_name}
              onChange={(e) => setNewTenant({ ...newTenant, admin_first_name: e.target.value })}
            />
            <TextField
              margin="dense"
              label="Admin Last Name"
              fullWidth
              variant="outlined"
              value={newTenant.admin_last_name}
              onChange={(e) => setNewTenant({ ...newTenant, admin_last_name: e.target.value })}
            />
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setCreateDialogOpen(false)}>Cancel</Button>
          <Button onClick={handleCreateTenant} variant="contained">
            Create Tenant
          </Button>
        </DialogActions>
      </Dialog>

      {/* Delete Confirm Dialog */}
      <Dialog
        open={deleteDialogOpen}
        onClose={() => {
          setDeleteDialogOpen(false);
          setSelectedTenant(null);
        }}
        maxWidth="xs"
        fullWidth
      >
        <DialogTitle>Delete tenant?</DialogTitle>
        <DialogContent>
          <Typography variant="body2">
            This will permanently delete <b>{selectedTenant?.name || 'this tenant'}</b> and all related data.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteDialogOpen(false)}>Cancel</Button>
          <Button
            color="error"
            variant="contained"
            onClick={() => selectedTenant && handleDeleteTenant(selectedTenant)}
          >
            Delete
          </Button>
        </DialogActions>
      </Dialog>

      {/* Edit Tenant Dialog */}
      <Dialog
        open={editDialogOpen}
        onClose={() => {
          setEditDialogOpen(false);
          setSelectedTenant(null);
        }}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>Edit Tenant</DialogTitle>
        <DialogContent>
          <TextField
            autoFocus
            margin="dense"
            label="Organization Name"
            fullWidth
            variant="outlined"
            value={editDraft.name}
            onChange={(e) => setEditDraft({ ...editDraft, name: e.target.value })}
          />
          <TextField
            margin="dense"
            label="Slug"
            fullWidth
            variant="outlined"
            value={editDraft.slug}
            onChange={(e) => setEditDraft({ ...editDraft, slug: e.target.value })}
            helperText="URL-friendly identifier (must be unique)"
          />
          <FormControlLabel
            control={
              <Switch
                checked={!!editDraft.is_active}
                onChange={(e) => setEditDraft({ ...editDraft, is_active: e.target.checked })}
              />
            }
            label="Active"
            sx={{ mt: 1 }}
          />
        </DialogContent>
        <DialogActions>
          <Button
            onClick={() => {
              setEditDialogOpen(false);
              setSelectedTenant(null);
            }}
          >
            Cancel
          </Button>
          <Button variant="contained" onClick={handleSaveEdit}>
            Save
          </Button>
        </DialogActions>
      </Dialog>

      {/* Actions Menu */}
      <Menu
        anchorEl={anchorEl}
        open={Boolean(anchorEl)}
        onClose={handleMenuClose}
      >
        <MenuItem
          onClick={() => {
            if (selectedTenant) openDetail(selectedTenant);
            handleMenuClose();
          }}
        >
          <PeopleIcon fontSize="small" sx={{ mr: 1 }} />
          View Details
        </MenuItem>
        <Divider />
        <MenuItem
          onClick={() => {
            if (selectedTenant) handleToggleStatus(selectedTenant);
            handleMenuClose();
          }}
        >
          {selectedTenant?.is_active
            ? <><BlockIcon fontSize="small" sx={{ mr: 1 }} /> Deactivate</>
            : <><ActivateIcon fontSize="small" sx={{ mr: 1 }} /> Activate</>}
        </MenuItem>
        <MenuItem
          onClick={() => {
            if (selectedTenant) openEdit(selectedTenant);
            handleMenuClose();
          }}
        >
          <EditIcon fontSize="small" sx={{ mr: 1 }} /> Edit
        </MenuItem>
        <MenuItem
          onClick={() => {
            setDeleteDialogOpen(true);
            handleMenuClose();
          }}
          sx={{ color: 'error.main' }}
        >
          <DeleteIcon fontSize="small" sx={{ mr: 1 }} /> Delete
        </MenuItem>
      </Menu>

      {/* Tenant detail drawer — uses detailTenant so handleMenuClose cannot clobber it */}
      <TenantDetailDrawer
        tenant={detailTenant}
        open={detailDrawerOpen}
        onClose={() => setDetailDrawerOpen(false)}
      />

      {/* Bulk delete confirmation */}
      <Dialog open={bulkDeleteOpen} onClose={() => setBulkDeleteOpen(false)} maxWidth="xs" fullWidth>
        <DialogTitle>Delete {rowSelectionModel.length} Tenant{rowSelectionModel.length > 1 ? 's' : ''}?</DialogTitle>
        <DialogContent>
          <Alert severity="warning" sx={{ mb: 1 }}>
            This will permanently delete all selected tenants, their users, and workflows. This cannot be undone.
          </Alert>
          <Typography variant="body2">
            <strong>{rowSelectionModel.length}</strong> tenant{rowSelectionModel.length > 1 ? 's' : ''} will be deleted.
            Your own organisation cannot be deleted and will be skipped.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setBulkDeleteOpen(false)} disabled={bulkDeleting}>Cancel</Button>
          <Button
            variant="contained"
            color="error"
            onClick={handleBulkDelete}
            disabled={bulkDeleting}
            startIcon={bulkDeleting ? <CircularProgress size={16} color="inherit" /> : <BulkDeleteIcon />}
          >
            {bulkDeleting ? 'Deleting…' : `Delete ${rowSelectionModel.length}`}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};